import axios from "axios";

const BASE = process.env.REACT_APP_API_URL || "http://localhost:8000";

const api = axios.create({ baseURL: BASE, timeout: 60000 });

api.interceptors.response.use(
  (r) => r,
  (err) => {
    const msg = err.response?.data?.detail || err.message || "Network error";
    if (err.response?.status === 401) {
      localStorage.removeItem("ds_token");
      window.location.href = "/login";
    }
    const wrapped = new Error(
      typeof msg === "string" ? msg : JSON.stringify(msg),
    );
    // Preserve the raw response body so callers that need structured detail
    // (e.g. upload validation errors/warnings) aren't limited to the
    // flattened string message.
    wrapped.data = err.response?.data;
    // Preserve the HTTP status and axios error code too, so callers can
    // distinguish a client-side timeout (ECONNABORTED, no status) from a
    // real server response, and detect a "file still busy" status.
    wrapped.status = err.response?.status;
    wrapped.code = err.code;
    return Promise.reject(wrapped);
  },
);

api.interceptors.request.use((config) => {
  const t = localStorage.getItem("ds_token");
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

export const register = (data) =>
  api.post("/auth/register", data).then((r) => r.data);
export const login = (data) =>
  api.post("/auth/login", data).then((r) => r.data);
export const verifyEmail = (token) =>
  api.get(`/auth/verify-email?token=${token}`).then((r) => r.data);
export const getMe = () => api.get("/auth/me").then((r) => r.data);
export const resendVerification = (email) =>
  api
    .post(`/auth/resend-verification?email=${encodeURIComponent(email)}`)
    .then((r) => r.data);

export const getConnections = () => api.get("/connections").then((r) => r.data);
export const getConnection = (id) =>
  api.get(`/connections/${id}`).then((r) => r.data);
export const createConnection = (data) =>
  api.post("/connections", data).then((r) => r.data);
export const updateConnection = (id, data) =>
  api.put(`/connections/${id}`, data).then((r) => r.data);
export const deleteConnection = (id) => api.delete(`/connections/${id}`);
export const validateConnection = (id) =>
  api.post(`/connections/${id}/validate`).then((r) => r.data);
export const getConnectionSchema = (id) =>
  api.get(`/connections/${id}/schema`).then((r) => r.data);

// Answering a question can involve multi-step reasoning plus SQL execution
// over the connected dataset, which legitimately runs 60-90s+ on larger
// tables — well past the client's default 60s timeout. Give these calls
// more room; the UI shows a "still working" state (see TypingIndicator)
// rather than relying on a short timeout to signal a problem.
const CHAT_TIMEOUT_MS = 180000;

// Excel/CSV processing time scales with file size, so a short fixed
// timeout makes the client report failure while the backend is still
// working — which is exactly what prompts a user to hit "upload" again and
// collide with their own still-running upload. Scale the timeout instead:
// a generous base for parsing/profiling overhead, plus a per-MB allowance,
// clamped to a sane range.
const UPLOAD_TIMEOUT_BASE_MS = 30000;
const UPLOAD_TIMEOUT_PER_MB_MS = 15000;
const UPLOAD_TIMEOUT_MIN_MS = 60000;
const UPLOAD_TIMEOUT_MAX_MS = 600000;

function uploadTimeoutForBytes(totalBytes) {
  const mb = totalBytes / (1024 * 1024);
  const scaled = UPLOAD_TIMEOUT_BASE_MS + mb * UPLOAD_TIMEOUT_PER_MB_MS;
  return Math.min(Math.max(scaled, UPLOAD_TIMEOUT_MIN_MS), UPLOAD_TIMEOUT_MAX_MS);
}

// A previous upload to the same filename can still be releasing its file
// handle on the backend when a new request lands. If the backend reports
// that distinctly (HTTP 423 Locked, or a detail mentioning the file being
// busy/locked), it's a transient collision — wait briefly and retry once,
// rather than surfacing it as a hard failure.
function isFileBusyError(err) {
  if (err.status === 423) return true;
  const detail = typeof err.data?.detail === "string" ? err.data.detail : err.message;
  return typeof detail === "string" && /file.*(busy|locked|in use)/i.test(detail);
}

async function postUploadWithBusyRetry(url, form, config) {
  try {
    return await api.post(url, form, config);
  } catch (err) {
    if (!isFileBusyError(err)) throw err;
    await new Promise((r) => setTimeout(r, 1500));
    return api.post(url, form, config);
  }
}

export const uploadFile = (file, name) => {
  const form = new FormData();
  form.append("file", file);
  form.append("name", name);
  return postUploadWithBusyRetry("/upload", form, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: uploadTimeoutForBytes(file.size),
  }).then((r) => r.data);
};

export const uploadRelationFiles = (files, fileType) => {
  const form = new FormData();
  files.forEach((file) => form.append("files", file));
  form.append("file_type", fileType);
  const totalBytes = files.reduce((sum, file) => sum + (file?.size || 0), 0);
  return postUploadWithBusyRetry("/relation-upload", form, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: uploadTimeoutForBytes(totalBytes),
  }).then((r) => r.data);
};
export const createRelationSession = (data) =>
  api.post("/relation-sessions", data).then((r) => r.data);
export const getRelationSessions = () =>
  api.get("/relation-sessions").then((r) => r.data);
export const updateRelationSessionActiveChat = (sessionId, activeChatId) =>
  api
    .patch(`/relation-sessions/${sessionId}/active-chat`, {
      active_chat_id: activeChatId,
    })
    .then((r) => r.data);
export const deleteRelationSession = (sessionId) =>
  api.delete(`/relation-sessions/${sessionId}`);
export const createRelationChat = (data) =>
  api.post("/relation-chats", data).then((r) => r.data);
export const updateRelationChat = (id, data) =>
  api.patch(`/relation-chats/${id}`, data).then((r) => r.data);
export const getRelationChats = (relationSessionId) =>
  api
    .get(`/relation-chats?relation_session_id=${relationSessionId}`)
    .then((r) => r.data);
export const deleteRelationChat = (chatId) =>
  api.delete(`/relation-chats/${chatId}`);
export const getRelationMessages = (relationChatId) =>
  api
    .get(`/relation-messages?relation_chat_id=${relationChatId}`)
    .then((r) => r.data);
export const sendRelationMessage = (data) =>
  api.post("/relation-messages", data, { timeout: CHAT_TIMEOUT_MS }).then((r) => r.data);

export const getChats = (connectionId) =>
  api.get(`/chats/${connectionId}`).then((r) => r.data);
export const createChat = (data) =>
  api.post("/chats", data).then((r) => r.data);
export const updateChat = (id, data) =>
  api.put(`/chats/${id}`, data).then((r) => r.data);
export const deleteChat = (id) => api.delete(`/chats/${id}`);

export const getMessages = (chatId) =>
  api.get(`/messages/${chatId}`).then((r) => r.data);
export const sendMessage = (chatId, content, options = {}) =>
  api
    .post(`/messages/${chatId}`, { content, ...options }, { timeout: CHAT_TIMEOUT_MS })
    .then((r) => r.data);

export const queryRelation = (data) =>
  api.post("/relation-query", data, { timeout: CHAT_TIMEOUT_MS }).then((r) => r.data);

export default api;
